import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Mail, Clock, Building, MessageCircle } from 'lucide-react';
import { branches } from '../branchesData';
import { useLanguage } from '../context/LanguageContext';

export const Branches = () => {
  const { t, isArabic } = useLanguage();
  const [selectedBranch, setSelectedBranch] = useState(null);

  const handleWhatsApp = (branch) => {
    const message = encodeURIComponent(`Hello! I'm interested in your services at ${branch.city}`);
    window.open(`https://wa.me/${branch.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20 pb-16 bg-white" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-16 fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-[#404040] mb-4">
            {t('ourGlobalPresence')}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('servingClients')}
          </p>
        </div>

        {/* Map Visual */}
        <div className="mb-16 relative h-96 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Building className="w-20 h-20 text-[#FF8C42] mx-auto mb-4" />
              <p className="text-2xl font-bold text-[#404040] mb-2">
                {isArabic ? '٥ فروع' : '5 Branches'}
              </p>
              <p className="text-gray-600">
                {isArabic ? 'في المملكة المتحدة والشرق الأوسط' : 'Across UK & Middle East'}
              </p>
            </div>
          </div>
        </div>

        {/* Branches Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {branches.map((branch) => (
            <Card 
              key={branch.id} 
              className={`border-none shadow-lg hover:shadow-xl transition-all cursor-pointer ${
                branch.isHeadquarters ? 'ring-2 ring-[#FF8C42]' : ''
              } ${selectedBranch?.id === branch.id ? 'ring-2 ring-[#FF8C42]' : ''}`}
              onClick={() => setSelectedBranch(branch)}
            >
              <CardContent className="p-6">
                {branch.isHeadquarters && (
                  <div className="bg-[#FF8C42] text-white text-xs px-3 py-1 rounded-full inline-block mb-3">
                    {isArabic ? 'المقر الرئيسي' : 'Headquarters'}
                  </div>
                )}
                
                <div className={`flex items-center mb-4 ${isArabic ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                  <div className="text-5xl">{branch.flag}</div>
                  <div>
                    <h3 className="text-xl font-bold text-[#404040]">
                      {isArabic ? branch.countryAr : branch.country}
                    </h3>
                    <p className="text-gray-600">
                      {isArabic ? branch.cityAr : branch.city}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className={`flex items-start ${isArabic ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <MapPin className="w-5 h-5 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      {isArabic ? branch.addressAr : branch.address}
                    </p>
                  </div>

                  <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <Phone className="w-5 h-5 text-[#FF8C42] flex-shrink-0" />
                    <a href={`tel:${branch.phone}`} className="text-sm text-gray-700 hover:text-[#FF8C42]">
                      {branch.phone}
                    </a>
                  </div>

                  <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <Mail className="w-5 h-5 text-[#FF8C42] flex-shrink-0" />
                    <a href={`mailto:${branch.email}`} className="text-sm text-gray-700 hover:text-[#FF8C42]">
                      {branch.email}
                    </a>
                  </div>

                  <div className={`flex items-start pt-2 border-t ${isArabic ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <Clock className="w-5 h-5 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600">
                      {isArabic ? branch.hours.ar : branch.hours.en}
                    </p>
                  </div>

                  {/* WhatsApp Button */}
                  {branch.whatsapp && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsApp(branch);
                      }}
                      className={`w-full bg-[#25D366] hover:bg-[#20BA5A] text-white mt-3`}
                    >
                      <MessageCircle className={`w-4 h-4 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                      {isArabic ? 'واتساب' : 'WhatsApp'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Google Maps Integration */}
        {selectedBranch && (
          <Card className="border-none shadow-2xl mb-12 fade-in">
            <CardContent className="p-6">
              <div className={`flex justify-between items-center mb-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <h3 className="text-xl font-bold text-[#404040]">
                  {selectedBranch.flag} {isArabic ? selectedBranch.cityAr : selectedBranch.city}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedBranch(null)}
                >
                  {t('close')}
                </Button>
              </div>
              <div className="relative w-full h-96 rounded-lg overflow-hidden">
                <iframe
                  src={selectedBranch.mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${selectedBranch.city} Map`}
                ></iframe>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">
            {isArabic ? 'تواصل مع أقرب فرع' : 'Get in Touch with Your Nearest Branch'}
          </h2>
          <p className="text-xl mb-6 opacity-90">
            {isArabic 
              ? 'فريقنا المتخصص جاهز لمساعدتك في جميع احتياجاتك العقارية'
              : 'Our expert team is ready to assist you with all your property needs'
            }
          </p>
          <p className="text-sm opacity-80">
            {isArabic 
              ? 'اضغط على أي فرع أعلاه لمشاهدة الموقع على الخريطة'
              : 'Click on any branch above to view location on map'
            }
          </p>
        </div>
      </div>
    </div>
  );
};
